// ============================================================
// Report Component
// Displays the full restructured report: opening synthesis,
// priority-ordered category sections with timed actions,
// closing 30/60/90/180-day path, action summary, personal note.
// Includes a simple bar chart and a download-as-PDF button.
// No scoring logic here — just rendering the JSON response.
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { getReport } from '../services/api';
import Feedback from './Feedback';

const CATEGORY_LABELS = {
  creativity: 'Creativity',
  humanJudgment: 'Human Judgment',
  adaptability: 'Adaptability',
  relationshipIntelligence: 'Relationship Intelligence',
  trust: 'Trust'
};

function Report({ sessionId, name, email }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef(null);

  useEffect(() => {
    getReport(sessionId)
      .then(setReport)
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [sessionId]);

  function handleDownload() {
    // Simple client-side print-to-PDF approach — opens the browser's
    // print dialog scoped to the report content, letting the user
    // save as PDF using their browser's own "Save as PDF" option.
    // No backend PDF generation needed for this version.
    window.print();
  }

  if (loading) return <div>Generating your CHART Rubric report...</div>;
  if (!report) return <div>Could not load report.</div>;

  return (
    <div className="report" ref={reportRef}>
      <div className="report-header no-print">
        <h2>Your CHART Rubric Report</h2>
        <button className="btn-secondary" onClick={handleDownload}>Download as PDF</button>
      </div>
      <h2 className="print-only">Your CHART Rubric Report</h2>

      <p className="overall-score">Overall Score: {report.overallScore} / 4</p>

      {/* Simple bar chart */}
      <div className="bar-chart">
        {report.priorityOrder.slice().reverse().map(cat => (
          <div key={cat} className="bar-row">
            <span className="bar-label">{CATEGORY_LABELS[cat]}</span>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${(report.categoryAverages[cat] / 4) * 100}%` }} />
            </div>
            <span className="bar-score">{report.categoryAverages[cat]}</span>
          </div>
        ))}
      </div>

      {/* Opening synthesis */}
      <div className="report-section">
        <h3>Where You Stand</h3>
        <p>{report.openingSynthesis}</p>
      </div>

      {/* Category sections, priority order */}
      {report.categorySections.map((section, idx) => (
        <div key={idx} className="category-section">
          <div className="category-section-header">
            <strong>{CATEGORY_LABELS[section.category]}</strong>
            <span className="category-score-tag">{section.score} / 4 — {section.tierLabel}</span>
          </div>
          <p className="category-rationale">{section.rationale}</p>

          {/* Timeline shown ONCE per category — all 3 actions below share
              this same urgency tier, so repeating the label per-action
              was purely redundant. */}
          {section.actions[0] && (
            <div className="action-timeline-heading">{section.actions[0].timeline}</div>
          )}

          <div className="action-list">
            {section.actions.map((action, aIdx) => (
              <div key={aIdx} className="action-item">
                <p>{action.text}</p>
              </div>
            ))}
          </div>

          {/* On screen: collapsible, click to expand.
              In print: always shown expanded (see .no-print/.print-only
              handling below) — a collapsed toggle means nothing on paper. */}
          <details className="question-detail-toggle no-print">
            <summary>See the reasoning behind this score</summary>
            <div className="question-insights">
              {section.questionInsights.map((text, qIdx) => (
                <p key={qIdx}>{text || 'No detail available for this question.'}</p>
              ))}
            </div>
          </details>
          <div className="question-detail-toggle print-only">
            <div className="print-reasoning-heading">The reasoning behind this score</div>
            <div className="question-insights">
              {section.questionInsights.map((text, qIdx) => (
                <p key={qIdx}>{text || 'No detail available for this question.'}</p>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Closing 30/60/90/180 path */}
      <div className="report-section">
        <h3>Your 30/60/90/180-Day Path</h3>
        <div className="path-block"><strong>First 30 days:</strong> <p>{report.closingPath.day30}</p></div>
        <div className="path-block"><strong>By day 60:</strong> <p>{report.closingPath.day60}</p></div>
        <div className="path-block"><strong>By day 90:</strong> <p>{report.closingPath.day90}</p></div>
        <div className="path-block"><strong>By day 180:</strong> <p>{report.closingPath.day180}</p></div>
      </div>

      {/* Action summary */}
      <div className="report-section action-summary">
        <h3>Your Action Summary</h3>
        <ul>
          {report.actionSummary.map((item, idx) => (
            <li key={idx}>
              <strong>{CATEGORY_LABELS[item.category]}:</strong> {item.topAction}
              <span className="summary-timeline"> ({item.timeline})</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Personal note */}
      <div className="personal-note">
        <h3>{report.personalNote.heading}</h3>
        <p>{report.personalNote.text}</p>
      </div>

      <div className="no-print">
        <Feedback name={name} email={email} />
      </div>
    </div>
  );
}

export default Report;
